import { RefObject, memo, useMemo } from 'react'

import {
  DerivationVariant,
  FileItem,
  FolderItem,
  ItemType,
  ShortcutItem
} from '@inno-sdks/files'
import { useIsTextTruncated } from '@inno-tools/react-hooks'
import { Avatar, AvatarSizes } from '@inno-ui/avatar'
import { Chip, ChipSizes } from '@inno-ui/chip'
import {
  ArrowDownLeftAndUpRightIcon,
  ChainLinkIcon,
  DocumentStackFullIcon,
  IconSizes,
  IconWeight,
  PersonFilledIcon,
  StarsFullIcon,
  TagFullIcon
} from '@inno-ui/icons'
import { Tooltip, TooltipPlacements } from '@inno-ui/tooltip'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'

import { useGetFileItem } from '../../api'
import { usePreviewPanelStore } from '../../store/previewPanelStore'
import { formatFileSize, getUserAvatarUrl } from '../../utils'
import styles from './PreviewPanel.module.scss'
import { PreviewSkeleton } from './PreviewSkeleton'
import { PreviewUnavailable } from './PreviewUnavailable'
import previewIllustration from './assets/previewIllustration.png'

export const PreviewPanel = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })

  const { isPreviewPanelOpen, selectedItemId } = usePreviewPanelStore()

  const { data: item, isLoading } = useGetFileItem(selectedItemId)

  const isFile = useMemo(() => item?.type === ItemType.File, [item?.type])
  const isFolder = useMemo(() => item?.type === ItemType.Folder, [item?.type])
  const isShortcut = useMemo(
    () => item?.type === ItemType.Shortcut,
    [item?.type]
  )

  const fileItem = useMemo(
    () => (item && isFile ? (item as FileItem) : null),
    [item, isFile]
  )
  const folderItem = useMemo(
    () => (item && isFolder ? (item as FolderItem) : null),
    [item, isFolder]
  )
  const shortcutItem = useMemo(
    () => (item && isShortcut ? (item as ShortcutItem) : null),
    [item, isShortcut]
  )

  const thumbnailUrl = useMemo(() => {
    if (!isFile || !fileItem?.metaData?.derivations) {
      return undefined
    }

    return fileItem.metaData.derivations.find(({ variant }) =>
      [DerivationVariant.Large, DerivationVariant.Medium].includes(variant)
    )?.signedUrl
  }, [isFile, fileItem?.metaData?.derivations])

  const excerpt = useMemo(
    () => (isFile && fileItem ? fileItem.extra?.excerpt || 'N/A' : ''),
    [isFile, fileItem]
  )

  const fileSize = useMemo(
    () => (isFile && fileItem ? (fileItem.metaData?.size ?? 0) : 0),
    [isFile, fileItem]
  )

  const shortcutUrl = useMemo(
    () => (shortcutItem ? shortcutItem.metaData.shortcutUrl : ''),
    [shortcutItem]
  )

  const tags = useMemo(() => item?.tags, [item?.tags])

  const formattedFileSize = useMemo(() => formatFileSize(fileSize), [fileSize])

  if (!isPreviewPanelOpen) {
    return null
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <PreviewSkeleton />
      </div>
    )
  }

  if (!selectedItemId || !item) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>
          <span>{t('click-to-preview')}</span>
          <img
            src={previewIllustration}
            alt='Preview Panel'
            width={400}
            height={400}
          />
        </div>
      </div>
    )
  }
  return (
    <div className={styles.container}>
      <div className={styles.previewPanelWrapper}>
        {isFile && (
          <div className={styles.previewSection}>
            {!thumbnailUrl || !item ? (
              <PreviewUnavailable />
            ) : (
              <div className={styles.thumbnailPreview}>
                <img
                  src={thumbnailUrl}
                  alt={item.name}
                  className={styles.previewImage}
                />
              </div>
            )}
          </div>
        )}

        {(isFolder || isShortcut) && <PreviewUnavailable />}

        <div className={styles.infoSection}>
          {isFile && (
            <div className={styles.infoRow}>
              <Tooltip
                overlay={t('excerpt')}
                placement={TooltipPlacements.Left}
              >
                <StarsFullIcon
                  color='var(--secondary-purple-light)'
                  weight={IconWeight.Bold}
                  size={IconSizes.Size14}
                />
              </Tooltip>

              <Excerpt key={selectedItemId} text={excerpt} />
            </div>
          )}

          {isShortcut && (
            <div className={styles.infoRow}>
              <Tooltip
                overlay={t('shortcut-url')}
                placement={TooltipPlacements.Left}
              >
                <ChainLinkIcon
                  weight={IconWeight.Bold}
                  size={IconSizes.Size14}
                />
              </Tooltip>

              <ShortcutUrl key={selectedItemId} url={shortcutUrl} />
            </div>
          )}

          <div className={styles.infoRow}>
            <Tooltip overlay={t('tags')} placement={TooltipPlacements.Left}>
              <TagFullIcon weight={IconWeight.Bold} size={IconSizes.Size14} />
            </Tooltip>

            {tags && tags.length > 0 ? (
              <div className={styles.chipGroup}>
                {tags.map(tag => (
                  <Chip key={tag.id} label={tag.name} size={ChipSizes.S} />
                ))}
              </div>
            ) : (
              <p className={styles.text}>N/A</p>
            )}
          </div>

          {isFile && (
            <div className={styles.infoRow}>
              <Tooltip overlay={t('size')} placement={TooltipPlacements.Left}>
                <ArrowDownLeftAndUpRightIcon
                  weight={IconWeight.Bold}
                  size={IconSizes.Size14}
                />
              </Tooltip>

              <p className={styles.text}>{formattedFileSize}</p>
            </div>
          )}

          {isFolder && folderItem && (
            <div className={styles.infoRow}>
              <Tooltip
                overlay={t('files-count')}
                placement={TooltipPlacements.Left}
              >
                <DocumentStackFullIcon
                  weight={IconWeight.Bold}
                  size={IconSizes.Size14}
                />
              </Tooltip>

              <p className={styles.text}>
                {t('files', { count: folderItem.filesCount })}
              </p>
            </div>
          )}

          {item.sharedBy && (
            <div className={styles.infoRow}>
              <Tooltip
                overlay={t('shared-by')}
                placement={TooltipPlacements.Left}
              >
                <PersonFilledIcon
                  weight={IconWeight.Bold}
                  size={IconSizes.Size14}
                />
              </Tooltip>

              <p className={styles.text}>{item.sharedBy.name}</p>

              <Avatar
                url={getUserAvatarUrl(item.sharedBy.avatar)}
                size={AvatarSizes.M}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

const Excerpt = memo(({ text }: { text: string }) => {
  const { isTruncated, textRef } = useIsTextTruncated()
  return (
    <Tooltip overlay={text} disabled={!isTruncated}>
      <p
        className={cn(styles.text, styles.excerpt)}
        ref={textRef as RefObject<HTMLParagraphElement>}
      >
        {text}
      </p>
    </Tooltip>
  )
})

const ShortcutUrl = memo(({ url }: { url: string }) => {
  const { isTruncated, textRef } = useIsTextTruncated()

  return (
    <Tooltip overlay={url} disabled={!isTruncated}>
      <a
        className={cn(styles.text, styles.shortcutUrl)}
        ref={textRef as RefObject<HTMLAnchorElement>}
        href={url}
        target='_blank'
        rel='noopener noreferrer'
      >
        {url}
      </a>
    </Tooltip>
  )
})
